/**
 * Filter Constants
 *
 * Default filter options and configurations for vulnerability filtering.
 * Extracted from index.tsx to centralize filter definitions.
 */

import { RiskSeverity, RiskStatus, SeverityDef } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { getPastTense } from '@/utils/text.util';
import {
  getStatusLabel,
  type VulnerabilityStatusMode,
} from '@/utils/vulnerabilityStatus.util';

/**
 * Boolean filter options for Yes/No filters (KEV, Exploits, SSO)
 */
export const BOOLEAN_FILTERS: DropdownOption[] = [
  {
    label: 'Yes',
    value: 'true',
    id: 'yes',
  },
  {
    label: 'No',
    value: 'false',
    id: 'no',
  },
];

/**
 * Get base status filter options (available to all users)
 * Uses mode-aware labels for proper display.
 *
 * @param mode - The current vulnerability status mode (legacy or new)
 * @returns Array of status filter options with mode-aware labels
 */
export function getBaseStatusFilters(
  mode: VulnerabilityStatusMode
): DropdownOption[] {
  return [
    {
      label: getPastTense(getStatusLabel(RiskStatus.Opened, mode)),
      value: RiskStatus.Opened,
      id: RiskStatus.Opened,
    },
    {
      label: getPastTense(getStatusLabel(RiskStatus.Remediated, mode)),
      value: RiskStatus.Remediated,
      id: RiskStatus.Remediated,
    },
    {
      label: getPastTense(getStatusLabel(RiskStatus.Accepted, mode)),
      value: RiskStatus.Accepted,
      id: RiskStatus.Accepted,
    },
    {
      label: getPastTense(getStatusLabel(RiskStatus.Deleted, mode)),
      value: RiskStatus.Deleted,
      id: RiskStatus.Deleted,
    },
  ];
}

/**
 * @deprecated Use getBaseStatusFilters(mode) instead
 * Kept for backward compatibility with existing code
 * Defaults to legacy mode
 */
export const BASE_STATUS_FILTERS: DropdownOption[] =
  getBaseStatusFilters('legacy');

/**
 * Get triaged status filter (Praetorian users only)
 * Uses mode-aware labels for proper display.
 *
 * @param mode - The current vulnerability status mode (legacy or new)
 * @returns Triaged status filter option with mode-aware label
 */
export function getTriagedStatusFilter(
  mode: VulnerabilityStatusMode
): DropdownOption {
  return {
    label: getStatusLabel(RiskStatus.Triaged, mode),
    value: RiskStatus.Triaged,
    id: RiskStatus.Triaged,
  };
}

/**
 * @deprecated Use getTriagedStatusFilter(mode) instead
 * Kept for backward compatibility with existing code
 * Defaults to legacy mode
 */
export const TRIAGED_STATUS_FILTER: DropdownOption =
  getTriagedStatusFilter('legacy');

/**
 * Severity filter options with severity tags for styling
 */
export const SEVERITY_FILTERS: DropdownOption[] = [
  {
    label: SeverityDef[RiskSeverity.Critical],
    value: RiskSeverity.Critical,
    id: RiskSeverity.Critical,
    severity: 'critical',
  },
  {
    label: SeverityDef[RiskSeverity.High],
    value: RiskSeverity.High,
    id: RiskSeverity.High,
    severity: 'high',
  },
  {
    label: SeverityDef[RiskSeverity.Medium],
    value: RiskSeverity.Medium,
    id: RiskSeverity.Medium,
    severity: 'medium',
  },
  {
    label: SeverityDef[RiskSeverity.Low],
    value: RiskSeverity.Low,
    id: RiskSeverity.Low,
    severity: 'low',
  },
  {
    label: SeverityDef[RiskSeverity.Info],
    value: RiskSeverity.Info,
    id: RiskSeverity.Info,
    severity: 'info',
  },
  {
    label: SeverityDef[RiskSeverity.Exposure],
    value: RiskSeverity.Exposure,
    id: RiskSeverity.Exposure,
    severity: 'info',
  },
];

/**
 * Default active columns for the vulnerability table
 */
export const DEFAULT_ACTIVE_COLUMNS = [
  'status',
  'priority',
  'name',
  'tags',
  'origins',
  'created',
  'visited',
] as const;

/**
 * Last Seen preset time window filter options.
 * Each value represents a duration string (e.g., '24h', '7d') that gets
 * converted to a timestamp comparison in useRisks.ts.
 */
export const LAST_SEEN_FILTERS: DropdownOption[] = [
  { label: '24 Hours', value: '24h', id: '24h' },
  { label: '48 Hours', value: '48h', id: '48h' },
  { label: '7 Days', value: '7d', id: '7d' },
  { label: '2 Weeks', value: '14d', id: '14d' },
  { label: '30 Days', value: '30d', id: '30d' },
];

/**
 * Range colors for CVSS/EPSS range selectors
 * Maps percentage thresholds to Tailwind CSS classes
 */
export const RANGE_SELECTOR_COLORS = {
  40: 'bg-chariot-stroke-info text-chariot-status-info',
  70: 'bg-chariot-stroke-medium text-chariot-status-medium',
  90: 'bg-chariot-stroke-high text-chariot-status-high',
  100: 'bg-chariot-stroke-critical text-chariot-status-critical',
} as const;
